import { useCallback, useEffect, useState } from "react"
import classNames from "classnames"

import { useDialogModal, DialogCommands } from "web/hooks/modals"

import debounce from "lodash.debounce"

export default function Button({
  disabled: disabledFromProps = false,
  variant = "primary_default",
  size = "medium",
  onClick = () => {},
  children,
  debounceMS = 100, // always prevent double-click
  disableAfterClick = false,
  confirm = "",
  id,
  testId,
  className,
}) {
  const [disabled, setDisabled] = useState(disabledFromProps)
  const dialogModal = useDialogModal()

  useEffect(() => {
    setDisabled(disabledFromProps)
  }, [disabledFromProps])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _onClick = useCallback(
    debounce(onClick, disableAfterClick ? Infinity : debounceMS, {
      leading: true,
    }),
    [onClick]
  )

  async function hasConfirmed() {
    const result = await dialogModal({
      title: "Please Confirm",
      prompt: confirm,
      commands: [DialogCommands.OK, DialogCommands.Cancel],
    })

    return result && result.response
  }

  async function handleClick() {
    if (confirm === "" || (await hasConfirmed())) _onClick()
  }

  // Classes mirror Components::ButtonClassHelper in app/helpers/components/button_class_helper.rb.
  // Keep the two in step. Medium stays a size up from the helper because React
  // pages run at 16px. Fixable once a full design pass is done on the other React components.
  const sizeClass = {
    tiny: "px-2 py-1 text-xs",
    small: "px-2 py-1 text-sm",
    medium: "px-2.5 py-1.5 text-base",
  }

  const variantClass = {
    primary_default:
      "bg-primary-4 hover:bg-primary-2 border text-white shadow-sm disabled:bg-gray-300 disabled:text-white",
    primary_danger:
      "bg-red-600 hover:bg-red-700 text-white shadow-sm disabled:bg-gray-300 disabled:text-white",
    secondary_default:
      "border border-primary-9 bg-white hover:bg-primary-10 text-primary-4 shadow-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:border-gray-300",
    secondary_danger:
      "border border-red-200 bg-white hover:bg-red-50 text-red-600 disabled:bg-gray-100 shadow-sm disabled:text-gray-500 disabled:border-gray-300",
    tertiary_default:
      "text-primary-4 hover:bg-primary-10 disabled:bg-transparent disabled:text-neutral-8",
  }

  const buttonClasses = classNames(
    className,
    "box-border inline-flex justify-center items-center rounded whitespace-nowrap transition-all cursor-pointer disabled:cursor-not-allowed font-medium",
    sizeClass[size],
    variantClass[variant]
  )

  return (
    <button
      data-testid={testId}
      id={id}
      className={buttonClasses}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()

        if (!disabled) {
          handleClick()
          setDisabled(disableAfterClick ? true : disabled)
        }
      }}
    >
      {children}
    </button>
  )
}
